import { useState, useRef, useCallback } from 'react';

// Drag-to-resize for a workspace's fixed-width detail-panel — the list-panel next to it just flexes to fill whatever's left.
// The width the user gives the panel is remembered in this browser (one width for the side panels, so it is the same on every page),
// and is used again when a page is opened later. storageKey: another key for a panel of its own (a different panel, not the side panel).
// direction 'left' = a right-hand panel (dragging left widens it); 'right' = a left-hand panel.
const readWidth = (key, initialWidth, min, max) => {
  try {
    const v = Number(localStorage.getItem(key));
    return Number.isFinite(v) && v > 0 ? Math.min(max, Math.max(min, v)) : initialWidth;
  } catch { return initialWidth; }
};

export function useResizablePanel(initialWidth = 420, min = 320, max = 720, direction = 'left', storageKey = 'nc-detail-panel-width') {
  const [width, setWidth] = useState(() => readWidth(storageKey, initialWidth, min, max));
  const [dragging, setDragging] = useState(false);
  const startRef = useRef({ x: 0, width: initialWidth });
  const lastRef = useRef(width);
  lastRef.current = width;

  const onMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      startRef.current = { x: e.clientX, width };
      setDragging(true);
      function onMove(ev) {
        const delta = direction === 'right' ? ev.clientX - startRef.current.x : startRef.current.x - ev.clientX;
        const next = Math.min(max, Math.max(min, startRef.current.width + delta));
        lastRef.current = next;
        setWidth(next);
      }
      function onUp() {
        setDragging(false);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        try { localStorage.setItem(storageKey, String(Math.round(lastRef.current))); } catch { /* not remembered */ }
      }
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [width, min, max, direction, storageKey]
  );

  return { width, dragging, onMouseDown };
}
